import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftIcon, UserGroupIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import GoCardlessUserImport from "../components/GoCardlessUserImport";
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from "../config";

const baseUrl = config.apiBaseUrl;

const ImportUsers = () => {
    const { provider } = useParams();
    const [existingSubscriptions, setExistingSubscriptions] = useState([]);
    const [loadingExisting, setLoadingExisting] = useState(true);
    const [venueKey, setVenueKey] = useState('');

    // Fetch existing subscriptions
    const fetchExistingSubscriptions = async () => {
        if (!venueKey) return;
        
        setLoadingExisting(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Subscription/UsersWithSubscription/${venueKey}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setExistingSubscriptions(data || []);
            } else {
                console.error('Failed to fetch existing subscriptions');
                setExistingSubscriptions([]);
            }
        } catch (error) {
            console.error('Error fetching existing subscriptions:', error);
            setExistingSubscriptions([]);
        } finally {
            setLoadingExisting(false);
        }
    };

    // Get venue key and fetch existing subscriptions on initial load
    useEffect(() => {
        const initializeData = async () => {
            try {
                const currentVenueKey = await Helper.getUserVenueId();
                setVenueKey(currentVenueKey);
                
                // Fetch existing subscriptions immediately after getting venue key
                if (currentVenueKey) {
                    setLoadingExisting(true);
                    try {
                        const token = await Helper.getCookie('wh-jwt');
                        const response = await fetch(`${baseUrl}/api/Subscription/UsersWithSubscription/${currentVenueKey}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });

                        if (response.ok) {
                            const data = await response.json();
                            setExistingSubscriptions(data || []);
                        } else {
                            console.error('Failed to fetch existing subscriptions');
                            setExistingSubscriptions([]);
                        }
                    } catch (error) {
                        console.error('Error fetching existing subscriptions:', error);
                        setExistingSubscriptions([]);
                    } finally {
                        setLoadingExisting(false);
                    }
                }
            } catch (error) {
                console.error('Error getting venue key:', error);
                setLoadingExisting(false);
            }
        };
        
        initializeData();
    }, []);

    const getProviderName = (provider) => {
        switch(provider) {
            case 'gocardless':
                return 'GoCardless';
            case 'stripe':
                return 'Stripe';
            case 'paypal':
                return 'PayPal';
            default:
                return provider?.charAt(0).toUpperCase() + provider?.slice(1);
        }
    };

    return (
        <LayoutWrapper 
            title={`Import ${getProviderName(provider)} Users`}
            subtitle={`Sync customer data from ${getProviderName(provider)} with your venue users`}
        >
            {/* Back Link */}
            <div className="mb-6">
                <Link 
                    to="/dashboard/paymentproviderlinks"
                    className="inline-flex items-center text-gray-400 hover:text-emerald-400 transition-colors duration-200"
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to Integrations
                </Link>
            </div>

            {/* Existing Subscriptions Section */}
            <div className="mb-8">
                <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                        <UserGroupIcon className="h-6 w-6 text-emerald-400" />
                        <div>
                            <h2 className="text-xl font-semibold text-white">Current Subscriptions</h2>
                            <p className="text-gray-400 text-sm">Users who already have linked subscriptions</p>
                        </div>
                    </div>

                    {loadingExisting ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mx-auto mb-4"></div>
                            <p className="text-gray-400">Loading existing subscriptions...</p>
                        </div>
                    ) : existingSubscriptions.length > 0 ? (
                        <>
                            <div className="mb-4 text-sm text-emerald-400 font-medium">
                                {existingSubscriptions.length} user{existingSubscriptions.length !== 1 ? 's' : ''} with linked subscriptions
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="border-b border-gray-700/30">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">User Email</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Subscription Name</th>
                                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-300">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700/30">
                                        {existingSubscriptions.map((subscription, index) => (
                                            <tr key={index} className="hover:bg-gray-800/30 transition-colors duration-200">
                                                <td className="px-4 py-3 text-sm text-white">{subscription.userEmail}</td>
                                                <td className="px-4 py-3 text-sm text-gray-300">{subscription.name}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-900/20 text-emerald-400 border border-emerald-800/30">
                                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                        Linked
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <UserGroupIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-white mb-2">No Linked Subscriptions</h3>
                            <p className="text-gray-400">No users have been linked to subscriptions yet.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Provider-specific Import Components */}
            {provider === 'gocardless' && <GoCardlessUserImport onUserSaved={fetchExistingSubscriptions} />}
            
            {provider === 'stripe' && (
                <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-8 text-center">
                    <h3 className="text-lg font-semibold text-white mb-2">Stripe Import Coming Soon</h3>
                    <p className="text-gray-400">Stripe user import functionality will be available in a future update.</p>
                </div>
            )}
            
            {provider === 'paypal' && (
                <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-8 text-center">
                    <h3 className="text-lg font-semibold text-white mb-2">PayPal Import Coming Soon</h3>
                    <p className="text-gray-400">PayPal user import functionality will be available in a future update.</p>
                </div>
            )}

            {!['gocardless', 'stripe', 'paypal'].includes(provider) && (
                <div className="backdrop-blur-sm bg-red-900/20 border border-red-800/30 rounded-xl p-8 text-center">
                    <h3 className="text-lg font-semibold text-white mb-2">Unknown Provider</h3>
                    <p className="text-red-300">The provider "{provider}" is not supported.</p>
                </div>
            )}
        </LayoutWrapper>
    );
};

export default ImportUsers;
